import { useTranslation } from 'react-i18next'
import { softBadgeColors } from '@structural-climbing/ui'
import {
  disciplineForSection,
  gradeDisplay,
  type Discipline,
} from '@structural-climbing/validation'
import { useGymGradeConfig } from '../../layouts/useGymGradeConfig'
import { ROUTE_COLORS } from '../routes/constants'

/** The setter's hold/tape colour name, matched by hex. Undefined for a custom one. */
export function holdColorName(hex: string | null | undefined): string | undefined {
  if (!hex) return undefined
  return ROUTE_COLORS.find((c) => c.value.toLowerCase() === hex.toLowerCase())?.label
}

function PhotoPlaceholder() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="h-8 w-8 text-foreground/20"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
      />
    </svg>
  )
}

interface RouteLike {
  id: string
  /** null for a route set for a competition, which is numbered, not graded. */
  grade: number | null
  color: string | null
  image_r2_key: string | null
  section?: { name: string; discipline: string | null } | null
  setter?: { name: string | null } | null
}

interface Props {
  route: RouteLike
  apiUrl: string
  /** Falls back to the route's own section when omitted. */
  discipline?: Discipline
  /** Shown under the grade — off where a section heading already says it. */
  showSection?: boolean
}

/**
 * How a route is identified everywhere in the competitions UI: photo, the grade
 * named as a grade, and the hold colour named as a colour.
 *
 * Shared rather than duplicated because the two are easy to confuse — a gym
 * whose grade scale is named after colours ends up with a grade called "Yellow"
 * beside black tape, and a bare swatch next to a bare label says nothing about
 * which is which.
 */
export function RouteIdentity({ route, apiUrl, discipline, showSection = false }: Props) {
  const { t } = useTranslation()
  const gradeConfig = useGymGradeConfig()

  const grade =
    route.grade === null
      ? null
      : gradeDisplay(
          gradeConfig,
          route.grade,
          discipline ?? disciplineForSection(route.section?.discipline),
        )
  const badge = grade ? softBadgeColors(grade.color) : null
  const hold = holdColorName(route.color)

  return (
    <>
      <div className="h-20 w-20 flex-none overflow-hidden rounded-md bg-default-100">
        {route.image_r2_key ? (
          <img
            src={`${apiUrl}/climbing-routes/${route.id}/image?size=thumb`}
            alt=""
            className="h-full w-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <PhotoPlaceholder />
          </div>
        )}
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex flex-wrap items-center gap-1.5">
          {/* A competition route has no grade: its number already names it. */}
          {grade && badge && (
            <span
              className="rounded px-2 py-0.5 text-xs font-bold"
              style={{ backgroundColor: badge.bg, color: badge.fg }}
            >
              {t('competitions.gradeLabel', { grade: grade.label })}
            </span>
          )}
          <span className="flex items-center gap-1 text-xs text-foreground/60">
            <span
              className="h-3 w-3 flex-none rounded-full border border-black/10 shadow-sm"
              style={{ backgroundColor: route.color ?? '#d4d4d8' }}
            />
            {hold ?? t('competitions.holdColor')}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-1.5 text-xs text-foreground/50">
          {showSection && route.section?.name && (
            <span className="rounded-full bg-default-100 px-2 py-0.5">{route.section.name}</span>
          )}
          {route.setter?.name && <span className="truncate">{route.setter.name}</span>}
        </div>
      </div>
    </>
  )
}
